import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { DRAFT_TARGETS, harmLabel, targetLabel } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft, Lightning, FilePdf, Download, CheckCircle, Trash, ClipboardText, Buildings, Target as TargetIcon, Calendar, CurrencyDollar, Clock, MapPin, ShieldCheck, EyeSlash } from "@phosphor-icons/react";
import EvidenceVault from "@/components/EvidenceVault";
import MassFileModal from "@/components/MassFileModal";
import { API } from "@/lib/api";
import { logger } from "@/lib/logger";

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cs, setCs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("demand_letter");
  const [generating, setGenerating] = useState(false);
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const [massFileOpen, setMassFileOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [shareNote, setShareNote] = useState("");

  const mountedRef = useRef(true);
  const abortRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Abort any in-flight SSE stream on unmount so the backend cancels Claude
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const safeSet = (fn) => { if (mountedRef.current) fn(); };

  const load = async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setCs(data);
    } catch (e) {
      toast.error("Case not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const generate = async (target) => {
    setGenerating(true);
    setElapsedSecs(0);
    // Abort any previous in-flight generation
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const token = localStorage.getItem("sf_token");
      const resp = await fetch(`${API}/cases/${id}/generate-draft/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ target }),
        signal: controller.signal,
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || `HTTP ${resp.status}`);
      }
      // Parse SSE stream
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finalCase = null;
      let errMsg = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Parse complete SSE events (delimited by blank line)
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const raw of parts) {
          const lines = raw.split("\n");
          let evt = "message";
          let data = "";
          for (const l of lines) {
            if (l.startsWith("event:")) evt = l.slice(6).trim();
            else if (l.startsWith("data:")) data += l.slice(5).trim();
          }
          if (!data) continue;
          try {
            const payload = JSON.parse(data);
            if (evt === "heartbeat") {
              safeSet(() => setElapsedSecs(payload.elapsed_seconds || 0));
            } else if (evt === "complete") {
              finalCase = payload;
            } else if (evt === "error") {
              errMsg = payload.detail || "AI generation failed";
            }
          } catch (err) { logger.error("SSE parse error", err); }
        }
      }
      if (errMsg) throw new Error(errMsg);
      if (!finalCase) throw new Error("Stream ended without result");
      safeSet(() => setCs(finalCase));
      safeSet(() => setActiveTab(target));
      if (mountedRef.current) toast.success(`${targetLabel(target)} generated.`);
    } catch (e) {
      if (e.name === "AbortError") return;
      // Detect 402 Payment Required → show upgrade nudge
      if (mountedRef.current) {
        const msg = e.message || "Generation failed";
        if (msg.includes("Full Arsenal not activated") || msg.includes("402")) {
          toast.error("Locked — activate Full Arsenal for $97 to generate drafts.");
        } else {
          toast.error(msg);
        }
      }
    } finally {
      safeSet(() => setGenerating(false));
      safeSet(() => setElapsedSecs(0));
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const logSent = async (target) => {
    try {
      const { data } = await api.post(`/cases/${id}/actions`, {
        target,
        label: `Filed: ${targetLabel(target)}`,
        note: "",
      });
      setCs(data);
      toast.success("Action logged.");
    } catch (e) {
      toast.error("Failed to log action");
    }
  };

  const download = async (target) => {
    const draft = cs.drafts[target];
    if (!draft) return;
    try {
      // Request a 60s HMAC-signed URL (no JWT in query string)
      const { data } = await api.post(`/cases/${cs.id}/drafts/${target}/sign`);
      const url = `${API.replace(/\/api$/, "")}${data.url}`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Failed to sign PDF URL");
    }
  };

  const downloadTxt = (target) => {
    const draft = cs.drafts[target];
    if (!draft) return;
    const blob = new Blob([draft.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${cs.corporation.replace(/\s+/g, "_")}_${target}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyDraft = async (target) => {
    const draft = cs.drafts[target];
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft.content);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed — browser blocked clipboard access.");
    }
  };

  const deleteCase = async () => {
    if (!confirm("Delete this case permanently?")) return;
    await api.delete(`/cases/${id}`);
    toast.success("Case deleted.");
    navigate("/dashboard");
  };

  const togglePublish = async (published, note) => {
    setPublishing(true);
    try {
      const body = { published };
      if (published) body.share_note = (note || "").slice(0, 280);
      await api.post(`/cases/${id}/publish`, body);
      setCs({ ...cs, published, share_note: body.share_note ?? cs.share_note });
      toast.success(published ? "Added to Wall of Friction." : "Removed from public wall.");
      setShowPublishDialog(false);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Full Arsenal required to publish wins.");
      } else {
        toast.error("Failed to update publish state");
      }
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white">
        <Nav />
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center font-mono-sf text-xs uppercase tracking-[0.3em] text-neutral-500">Loading case...</div>
      </div>
    );
  }
  if (!cs) return null;

  // Split drafts into fixed six-front + dynamic state_ag_XX (mass-file)
  const stateAgDraftKeys = Object.keys(cs.drafts || {})
    .filter((k) => k.startsWith("state_ag_") && k.length > 9)
    .sort();
  const draftLabel = (k) => {
    if (k.startsWith("state_ag_")) {
      const code = k.slice(9);
      return `State AG · ${code}`;
    }
    return targetLabel(k);
  };
  const draftShort = (k) => {
    if (k.startsWith("state_ag_")) return `AG ${k.slice(9)}`;
    return DRAFT_TARGETS.find((t) => t.value === k)?.short || k.toUpperCase();
  };
  const draftDesc = (k) => {
    if (k.startsWith("state_ag_")) return `State Attorney General complaint — ${k.slice(9)}`;
    return DRAFT_TARGETS.find((t) => t.value === k)?.desc || "";
  };

  const activeDraft = cs.drafts?.[activeTab];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white" data-testid="case-detail-page">
      <Nav />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-6">
          <Link to="/dashboard" className="inline-flex items-center gap-2 font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[#FF3366] transition-colors" data-testid="back-link">
            <ArrowLeft size={14} weight="bold" /> Cases
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMassFileOpen(true)}
              className="inline-flex items-center gap-2 h-9 px-3 border border-[#FF3366]/40 hover:bg-[#FF3366]/10 hover:text-[#FF3366] text-[#FF3366] font-mono-sf text-[10px] uppercase tracking-[0.25em] transition-colors"
              data-testid="mass-file-open-btn"
            >
              <MapPin size={12} weight="bold" /> Mass File
            </button>
            <button
              onClick={() => cs.published ? togglePublish(false) : setShowPublishDialog(true)}
              disabled={publishing}
              className={`inline-flex items-center gap-2 h-9 px-3 border font-mono-sf text-[10px] uppercase tracking-[0.25em] transition-colors ${
                cs.published
                  ? "border-[#00FFCC]/40 text-[#00FFCC] hover:bg-[#00FFCC]/10"
                  : "border-[#1A1A1A] hover:border-[#00FFCC] hover:text-[#00FFCC]"
              }`}
              data-testid="publish-toggle-btn"
            >
              {cs.published ? <><EyeSlash size={12} weight="bold" /> Unpublish</> : <><ShieldCheck size={12} weight="bold" /> Publish to Wall</>}
            </button>
            <button
              onClick={deleteCase}
              className="inline-flex items-center gap-2 h-9 px-3 border border-[#1A1A1A] hover:border-[#FF3366] hover:text-[#FF3366] font-mono-sf text-[10px] uppercase tracking-[0.25em] transition-colors"
              data-testid="delete-case-btn"
            >
              <Trash size={12} weight="bold" /> Delete
            </button>
          </div>
        </div>

        {/* Case header */}
        <div className="border border-[#1A1A1A] p-6 md:p-8 mb-8" data-testid="case-header">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-2 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#FF3366] border border-[#FF3366]/40 px-2 py-1">
              <span className="w-1.5 h-1.5 bg-[#FF3366] animate-pulse" />
              {cs.status}
            </span>
            <span className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-500">
              CASE-{cs.id.slice(0, 8).toUpperCase()}
            </span>
          </div>
          <h1 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-5xl leading-none mb-2" data-testid="case-title">{cs.title}</h1>
          <div className="font-mono-sf text-sm text-neutral-400 mb-6 flex items-center gap-2">
            <Buildings size={14} weight="bold" /> vs. <span className="text-white font-semibold">{cs.corporation}</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
            {[
              { i: TargetIcon, l: "Harm", v: harmLabel(cs.harm_type) },
              { i: CurrencyDollar, l: "Damages", v: `$${(cs.estimated_damages || 0).toLocaleString()}` },
              { i: Calendar, l: "Incident", v: cs.incident_date || "—" },
              { i: Clock, l: "Opened", v: new Date(cs.created_at).toLocaleDateString() },
            ].map((m) => {
              const Ic = m.i;
              return (
                <div key={m.l} className="bg-[#0D0D0D] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Ic size={12} weight="bold" className="text-[#FF3366]" />
                    <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">{m.l}</span>
                  </div>
                  <div className="font-mono-sf text-sm font-semibold">{m.v}</div>
                </div>
              );
            })}
          </div>

          <details className="mt-6 group">
            <summary className="cursor-pointer font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-400 hover:text-[#FF3366]" data-testid="case-narrative-toggle">
              View narrative
            </summary>
            <p className="mt-4 text-sm text-neutral-300 whitespace-pre-wrap font-mono-sf leading-relaxed" data-testid="case-narrative">{cs.description}</p>
          </details>
        </div>

        {/* Evidence Vault */}
        <div className="mt-8">
          <EvidenceVault caseId={cs.id} />
        </div>

        {/* Drafts arsenal */}
        <div className="grid lg:grid-cols-[280px_1fr] gap-px bg-[#1A1A1A] border border-[#1A1A1A] mb-10 mt-8">
          {/* Sidebar */}
          <div className="bg-[#0D0D0D] divide-y divide-[#1A1A1A]" data-testid="drafts-sidebar">
            <div className="p-4">
              <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">Arsenal</div>
              <div className="font-heading font-bold uppercase text-lg mt-1">Six Fronts</div>
            </div>
            {DRAFT_TARGETS.map((t) => {
              const generated = !!cs.drafts?.[t.value];
              const active = activeTab === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setActiveTab(t.value)}
                  className={`w-full text-left p-4 transition-colors ${active ? "bg-[#FF3366] text-white" : "hover:bg-[#1A1A1A]"}`}
                  data-testid={`tab-${t.value}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-mono-sf text-[10px] uppercase tracking-[0.25em] ${active ? "text-white/80" : "text-neutral-500"}`}>{t.short}</span>
                    {generated && <CheckCircle size={14} weight="fill" className={active ? "text-white" : "text-[#FF3366]"} />}
                  </div>
                  <div className="font-heading font-bold uppercase tracking-tight text-sm">{t.label}</div>
                </button>
              );
            })}
            {stateAgDraftKeys.length > 0 && (
              <div className="p-4 bg-[#0D0D0D]">
                <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#00FFCC] mb-1">Mass-Filed</div>
                <div className="font-heading font-bold uppercase text-sm text-neutral-400">{stateAgDraftKeys.length} state AGs</div>
              </div>
            )}
            {stateAgDraftKeys.map((k) => {
              const active = activeTab === k;
              const code = k.slice(9);
              return (
                <button
                  key={k}
                  onClick={() => setActiveTab(k)}
                  className={`w-full text-left p-4 transition-colors ${active ? "bg-[#00FFCC] text-[#0D0D0D]" : "hover:bg-[#1A1A1A]"}`}
                  data-testid={`tab-${k}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-mono-sf text-[10px] uppercase tracking-[0.25em] ${active ? "text-[#0D0D0D]/70" : "text-neutral-500"}`}>AG · {code}</span>
                    <CheckCircle size={14} weight="fill" className={active ? "text-[#0D0D0D]" : "text-[#00FFCC]"} />
                  </div>
                  <div className="font-heading font-bold uppercase tracking-tight text-sm">State AG {code}</div>
                </button>
              );
            })}
          </div>

          {/* Draft viewer */}
          <div className="bg-[#0D0D0D] p-6 md:p-8" data-testid="draft-viewer">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-[#1A1A1A]">
              <div>
                <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#FF3366]">
                  // {draftShort(activeTab)}
                </div>
                <h2 className="font-heading font-bold uppercase tracking-tight text-2xl mt-1">
                  {draftLabel(activeTab)}
                </h2>
                <p className="text-xs text-neutral-500 font-mono-sf mt-1">
                  {draftDesc(activeTab)}
                </p>
              </div>
              {activeDraft ? (
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => copyDraft(activeTab)} className="h-9 px-3 border border-[#1A1A1A] hover:bg-[#1A1A1A] font-mono-sf text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 transition-colors" data-testid="copy-draft-btn">
                    <ClipboardText size={12} weight="bold" /> Copy
                  </button>
                  <button onClick={() => downloadTxt(activeTab)} className="h-9 px-3 border border-[#1A1A1A] hover:bg-[#1A1A1A] font-mono-sf text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 transition-colors" data-testid="download-txt-btn">
                    <Download size={12} weight="bold" /> .TXT
                  </button>
                  <button onClick={() => download(activeTab)} className="h-9 px-4 bg-[#00FFCC] hover:bg-[#33ffd4] text-[#0D0D0D] font-mono-sf text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2 transition-colors" data-testid="download-pdf-btn">
                    <FilePdf size={12} weight="bold" /> Stream PDF
                  </button>
                  <button onClick={() => logSent(activeTab)} className="h-9 px-3 bg-[#FF3366] hover:bg-[#FF5248] text-white font-mono-sf text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 font-bold transition-colors" data-testid="mark-sent-btn">
                    <CheckCircle size={12} weight="bold" /> Mark Filed
                  </button>
                  <button onClick={() => generate(activeTab)} disabled={generating} className="h-9 px-3 border border-[#1A1A1A] hover:bg-[#1A1A1A] disabled:opacity-50 font-mono-sf text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 transition-colors" data-testid="regenerate-btn">
                    <Lightning size={12} weight="bold" /> Regenerate
                  </button>
                </div>
              ) : (
                <button onClick={() => generate(activeTab)} disabled={generating} className="h-10 px-4 bg-[#FF3366] hover:bg-[#FF5248] disabled:opacity-50 text-white font-mono-sf text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2 transition-colors" data-testid="generate-btn">
                  <Lightning size={14} weight="bold" /> {generating ? "Drafting..." : "Generate Draft"}
                </button>
              )}
            </div>

            {generating && !activeDraft ? (
              <div className="font-mono-sf text-sm text-[#FF3366] cursor-blink" data-testid="generating-state">
                claude-sonnet-4.5 :: drafting {activeTab} :: {elapsedSecs}s elapsed
              </div>
            ) : activeDraft ? (
              <div>
                <pre
                  className="font-mono-sf text-sm leading-relaxed whitespace-pre-wrap text-neutral-200 bg-[#0D0D0D] border border-[#1A1A1A] p-5 max-h-[600px] overflow-y-auto"
                  data-testid="draft-content"
                >{activeDraft.content}</pre>
                <div className="mt-3 font-mono-sf text-[10px] uppercase tracking-wider text-neutral-600">
                  Generated {new Date(activeDraft.generated_at).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-[#1A1A1A] p-12 text-center" data-testid="empty-draft-state">
                <Lightning size={36} weight="bold" className="mx-auto text-[#FF3366] mb-4" />
                <div className="font-heading font-bold uppercase tracking-tight text-xl mb-2">No draft yet.</div>
                <p className="text-neutral-500 text-sm font-mono-sf">Click Generate to draft this front.</p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="border border-[#1A1A1A] p-6 md:p-8" data-testid="timeline-section">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-heading font-bold uppercase tracking-tight text-2xl">Action Timeline</h2>
            <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">{cs.actions?.length || 0} events</span>
          </div>
          {(cs.actions?.length || 0) === 0 ? (
            <p className="text-sm text-neutral-500 font-mono-sf" data-testid="empty-timeline">No actions logged yet. Generate a draft and mark it filed.</p>
          ) : (
            <ol className="relative border-l border-[#1A1A1A] ml-2 space-y-6">
              {cs.actions.map((a) => (
                <li key={a.id} className="ml-6" data-testid={`timeline-${a.id}`}>
                  <div className="absolute -left-[6px] w-3 h-3 bg-[#FF3366]" />
                  <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1">
                    {new Date(a.timestamp).toLocaleString()}
                  </div>
                  <div className="font-heading font-bold uppercase tracking-tight text-base">{a.label}</div>
                  {a.note && <p className="text-sm text-neutral-400 mt-1">{a.note}</p>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
      <MassFileModal
        caseId={cs.id}
        open={massFileOpen}
        onClose={() => setMassFileOpen(false)}
        onDone={(fresh) => { setCs(fresh); setMassFileOpen(false); }}
      />
      {showPublishDialog && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="publish-dialog">
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] w-full max-w-lg">
            <div className="p-6 border-b border-[#1A1A1A]">
              <div className="flex items-center gap-2 mb-3">
                <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#00FFCC] border border-[#00FFCC]/40 px-2 py-1">
                  WALL OF FRICTION
                </span>
              </div>
              <h3 className="font-heading font-black uppercase tracking-tighter text-2xl">
                Add your <span className="text-[#00FFCC]">receipt</span> to the wall.
              </h3>
              <p className="text-xs text-neutral-500 font-mono-sf mt-2 leading-relaxed">
                Public post shows only: corporation, harm type, damages bucket, and this note.
                Your name, narrative, and evidence stay private.
              </p>
            </div>
            <div className="p-6 border-b border-[#1A1A1A]">
              <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                Public note (optional, 280 chars)
              </label>
              <textarea
                value={shareNote}
                onChange={(e) => setShareNote(e.target.value.slice(0, 280))}
                rows={3}
                placeholder="e.g. Filed 4 complaints, got the fees waived and $500 back in 3 weeks."
                className="w-full bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#00FFCC] px-3 py-2 text-sm font-mono-sf text-white outline-none resize-none"
                data-testid="publish-share-note"
              />
              <div className="text-right text-[10px] font-mono-sf text-neutral-600 mt-1">{shareNote.length}/280</div>
            </div>
            <div className="p-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowPublishDialog(false)}
                className="h-10 px-4 border border-[#1A1A1A] hover:bg-[#1A1A1A] text-xs font-mono-sf uppercase tracking-[0.2em] transition-colors"
                data-testid="publish-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => togglePublish(true, shareNote)}
                disabled={publishing}
                className="h-10 px-5 bg-[#00FFCC] hover:bg-[#33ffd4] text-[#0D0D0D] text-xs font-mono-sf uppercase tracking-[0.2em] font-bold disabled:opacity-50 transition-colors"
                data-testid="publish-confirm-btn"
              >
                {publishing ? "Publishing…" : "Publish to Wall"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
